/**
 * Tiny react-query-shaped data-fetching hook with a fallback for dev
 * mode where the backend isn't running.
 *
 * Why not pull in @tanstack/react-query? Bundle size + we already keep
 * the dependency surface small (axios + react + react-router). The
 * surface area this hook needs is ~20 lines and built-ins do fine.
 *
 * Usage:
 *   const { data, error, loading, refetch } = useApi(
 *       () => leadsService.list({ page: 0 }),
 *       { devFallback: MOCK_LEADS, deps: [page] });   // DEV-only demo data
 *       // ...or `fallback: []` for an empty shape that must exist in prod too.
 *
 * Pages keep their existing mock arrays as `fallback` so the UI stays
 * pixel-identical when the backend is unreachable (e.g. design review).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useApi(fetcher, { fallback = null, devFallback = null, deps = [], enabled = true } = {}) {
  /**
   * ── Two different ideas used to share one parameter (D50) ──
   *
   * `fallback`    an EMPTY SHAPE — [], {}, EMPTY_PLAN. Its job is to stop
   *               `.map()` crashing before the first fetch resolves. Correct
   *               in every build.
   * `devFallback` DEMO DATA — fabricated records that make a screen look
   *               populated with the backend down. DEV only, always.
   *
   * Both went in as `fallback`, and the initial state was seeded with it
   * unguarded, so production booted holding eight invented appointments and a
   * complete invented analytics page. The catch block below was already
   * DEV-guarded and its comment already claimed "production shows an
   * empty/error state instead" — but the guard only stopped the mock being
   * RE-applied. Nothing ever cleared the copy that was there from line one.
   *
   * Note what this deliberately does NOT do: clear `data` on a failed refetch.
   * Replacing good rows with an empty array on a transient error is worse than
   * showing slightly stale ones — the same reasoning as CrmProvider's customer
   * refresh. Callers get `error` and decide.
   */
  const seed = import.meta.env.DEV ? (devFallback ?? fallback) : fallback;
  const [data, setData]       = useState(seed);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(enabled);
  // Bumping this re-runs the fetcher even when deps haven't changed.
  const refetchKey            = useRef(0);
  const [tick, setTick]       = useState(0);

  const refetch = useCallback(() => {
    refetchKey.current += 1;
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(fetcher)
      .then((result) => {
        if (cancelled) return;
        // Some backend endpoints return { data: ... }, others return arrays
        // directly. Unwrap one level of {data} for convenience but only when
        // the result actually has that shape.
        const unwrapped =
          result && typeof result === 'object' && 'data' in result && !Array.isArray(result)
            ? result.data
            : result;
        setData(unwrapped);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        // DEV only. In a build, substituting mock data for a failed fetch makes
        // a broken backend indistinguishable from a working one: the page
        // renders convincing rows, the only trace is a console warning, and
        // real records look like they were never created. Callers already
        // receive `error`, so production shows an empty/error state instead.
        if (devFallback !== null && import.meta.env.DEV) {
          setData(devFallback);
          console.warn('[useApi] fetch failed, using dev fallback', err?.message);
        } else {
          console.warn('[useApi] fetch failed', err?.message);
        }
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tick, ...deps]);

  return { data, error, loading, refetch };
}

/**
 * Mutation helper — wraps a server call so the caller gets
 * { mutate, mutating, lastResult, lastError } without rolling their own
 * useState dance.
 */
export function useMutation(fn) {
  const [mutating, setMutating]     = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError]   = useState(null);

  const mutate = useCallback(async (...args) => {
    setMutating(true);
    setLastError(null);
    try {
      const r = await fn(...args);
      setLastResult(r);
      return r;
    } catch (err) {
      setLastError(err);
      throw err;
    } finally {
      setMutating(false);
    }
  }, [fn]);

  return { mutate, mutating, lastResult, lastError };
}
